import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/use-auth';
import { useCoinBalance } from '@/context/use-coin-balance';
import { useAction, useQuery } from "convex/react";
import { api } from "../../../backend/convex/_generated/api";

interface CoinPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  priceDisplay: string;
  popular?: boolean;
  savings?: string;
}

export function Billing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signIn } = useAuth();
  const { refreshBalance } = useCoinBalance();
  const createCheckout = useAction(api.stripe.createCheckoutSession);
  const creditPackages = useQuery(api.stripe.getCreditPackages);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('credits-1500');

  const returnUrl = (location.state as { returnUrl?: string })?.returnUrl || '/';

  useEffect(() => {
    if (!isAuthenticated) signIn();
  }, [isAuthenticated, signIn]);

  const handlePurchase = async (pkg: CoinPackage) => {
    if (!user) { signIn(); return; }

    setIsLoading(pkg.id);

    try {
      const result = await createCheckout({
        clerkId: user.id,
        packageId: pkg.id,
        successUrl: `${window.location.origin}/billing/success?returnUrl=${encodeURIComponent(returnUrl)}`,
        cancelUrl: `${window.location.origin}/billing?returnUrl=${encodeURIComponent(returnUrl)}`,
      });

      if (result.success && result.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        console.error('Checkout failed:', result.error);
      }

      // Refresh balance after successful purchase
      refreshBalance();
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setIsLoading(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-secondary)] font-ui">Loading...</p>
        </div>
      </div>
    );
  }

  const selectedPkg = creditPackages?.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--bg-primary)]/95 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <button 
            onClick={() => navigate(returnUrl)}
            className="p-2 -ml-2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <span className="font-ui font-semibold text-[var(--text-primary)] text-sm">Get Coins</span>
          
          <div className="w-9" />
        </div>
      </header>

      <div className="h-14" />

      {/* Main Content */}
      <main className="px-4 py-6 max-w-md mx-auto">
        {/* Pricing Cards */}
        <div className="space-y-3 mb-8">
          {creditPackages?.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => setSelectedPlan(pkg.id)}
              disabled={isLoading === pkg.id}
              className={`w-full relative p-4 rounded-2xl border-2 transition-all duration-200 text-left
                ${selectedPlan === pkg.id
                  ? 'bg-[var(--accent)]/5 border-[var(--accent)]' 
                  : 'bg-[var(--bg-secondary)] border-[var(--border)] hover:border-[var(--accent)]/30'
                }
                ${isLoading === pkg.id ? 'opacity-70' : ''}
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Radio Button */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                    ${selectedPlan === pkg.id
                      ? 'border-[var(--accent)] bg-[var(--accent)]'
                      : 'border-[var(--border)]'
                    }
                  `}>
                    {selectedPlan === pkg.id && (
                      <div className="w-2 h-2 rounded-full bg-[var(--bg-primary)]" />
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-body text-xl font-bold text-[var(--text-primary)]">
                        {pkg.name}
                      </span>
                      <span className="font-ui text-sm text-[var(--text-tertiary)]">coins</span>
                    </div>
                    {pkg.savings && (
                      <span className="inline-block mt-1 font-ui text-[10px] font-medium text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                        {pkg.savings}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <span className="font-ui text-lg font-bold text-[var(--text-primary)]">
                    {pkg.priceDisplay}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* CTA Button */}
        <button
          onClick={() => selectedPkg && handlePurchase(selectedPkg)}
          disabled={!selectedPkg || isLoading !== null}
          className="w-full py-4 rounded-2xl font-ui text-base font-semibold text-[var(--bg-primary)] bg-[var(--accent)] hover:bg-[var(--accent)]/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-[var(--bg-primary)] border-t-transparent rounded-full animate-spin" />
              Processing...
            </span>
          ) : (
            `Buy ${selectedPkg?.name || ''} Coins`
          )}
        </button>

        {/* Powered by Stripe */}
        <p className="mt-6 text-center font-ui text-[11px] text-[var(--text-tertiary)]">
          Powered by Stripe
        </p>
      </main>
    </div>
  );
}
